
import React, { memo, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/SearchBar";
import QuickFilters from "./QuickFilters";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import type { Category } from "@/services/promptService";

interface FilterSectionProps {
  categories: Category[];
  selectedCategory: string | null;
  onCategoryFilter: (categoryId: string) => void;
  onSearch: (query: string) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  disabled?: boolean;
  // New filter props
  selectedSort?: string;
  selectedTimeFilter?: string;
  onSortChange?: (sort: string) => void;
  onTimeFilterChange?: (timeFilter: string) => void;
}

const FilterSection: React.FC<FilterSectionProps> = memo(({
  categories,
  selectedCategory,
  onCategoryFilter,
  onSearch,
  hasFilters,
  onClearFilters,
  disabled = false,
  selectedSort = 'relevance',
  selectedTimeFilter = '',
  onSortChange = () => {},
  onTimeFilterChange = () => {}
}) => {
  // Format category name for display
  const formatCategoryName = useCallback((name: string) => {
    return name;
  }, []);

  // Memoize category click handler with unselect functionality
  const handleCategoryClick = useCallback((categoryId: string) => {
    // If the category is already selected, unselect it by passing null
    if (selectedCategory === categoryId) {
      onCategoryFilter(''); // Pass empty string to unselect
    } else {
      onCategoryFilter(categoryId);
    }
  }, [onCategoryFilter, selectedCategory]);

  return (
    <header className="mb-10">
      <h1 className="text-3xl font-bold mb-6">Explore Prompts</h1>
      
      <SearchBar 
        onSearch={onSearch} 
        placeholder="Search prompts..."
        className="mb-8"
        disabled={disabled}
      />
      
      {/* Quick Filters Only */}
      <div className="mb-8">
        <QuickFilters
          selectedSort={selectedSort}
          selectedTimeFilter={selectedTimeFilter}
          onSortChange={onSortChange}
          onTimeFilterChange={onTimeFilterChange}
          disabled={disabled}
        />
        
        <Separator className="mt-6" />
      </div>
      
      {/* Category Filters */}
      <div className="mb-8">
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Categories</h3>
        <div className="flex flex-wrap gap-2">
          {categories.length === 0 ? (
            // Show skeleton loaders during initial loading
            <div className="w-full flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Skeleton key={i} className="h-8 w-24 rounded-full" />
              ))}
            </div>
          ) : (
            // Show actual categories when loaded
            categories.map(category => (
              <Badge
                key={category.id}
                variant={selectedCategory === category.id ? "default" : "outline"}
                className={`px-3 py-1 cursor-pointer text-xs sm:text-sm whitespace-normal mb-1 ${disabled ? 'opacity-70' : ''}`}
                onClick={() => !disabled && handleCategoryClick(category.id)}
              >
                {formatCategoryName(category.name)}
              </Badge>
            ))
          )}
        </div>
      </div>
      
      {hasFilters && (
        <div className="flex justify-end mb-4">
          <Button 
            variant="ghost" 
            onClick={onClearFilters}
            disabled={disabled}
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </header>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to optimize re-renders
  return (
    prevProps.selectedCategory === nextProps.selectedCategory &&
    prevProps.categories.length === nextProps.categories.length &&
    prevProps.hasFilters === nextProps.hasFilters &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.selectedSort === nextProps.selectedSort &&
    prevProps.selectedTimeFilter === nextProps.selectedTimeFilter
  );
});

FilterSection.displayName = "FilterSection";

export default FilterSection;
